import React from "react";
import {
  toNumber,
  getTruckTypeLabel,
} from "../../../utils/loadFormatting.js";
import {
  capitalizeCompanyName,
  getDataSourceBadge,
} from "../../../utils/formatting.js";
import { getLoadIdentifier } from "../../../domain/load.js";

// Maximum number of characters to display for the Broker field.
// Anything longer will be truncated with "..."
const MAX_BROKER_LENGTH = 9;

const ResultsTable = ({
  results,
  onSelectLoad,
  highlightMap = {},
  showPartners = false,
  viewedLoadIds = new Set(),
  isSelectMode = false,
  selectedLoadIds = new Set(),
  onToggleLoadSelection,
}) => {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  return (
    <table className="w-full bg-white text-sm min-w-max">
      <thead className="bg-tw-neutral-50 sticky top-0 border-b border-gray-200">
        <tr className="text-sm text-tw-neutral-600">
          {isSelectMode && (
            <th className="w-10 px-3 py-2.5">
              <span className="sr-only">Select</span>
            </th>
          )}
          <th className="text-left px-3 py-2.5 font-medium">
            <span className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full bg-tw-info flex-shrink-0"
                title="Blue dot indicates unviewed loads"
              />
              <span>Age</span>
            </span>
          </th>
          <th className="text-left px-3 py-2.5 font-medium">Pickup City</th>
          <th className="text-left px-3 py-2.5 font-medium">Delivery City</th>
          <th className="text-left px-3 py-2.5 font-medium">Price</th>
          <th className="text-left px-3 py-2.5 font-medium">RpM</th>
          <th className="text-left px-3 py-2.5 font-medium">RpM (w/ DH)</th>
          <th className="text-left px-3 py-2.5 font-medium">Deadhead</th>
          <th className="text-left px-3 py-2.5 font-medium">Distance</th>
          <th className="text-left px-3 py-2.5 font-medium">Weight (lbs)</th>
          <th className="text-left px-3 py-2.5 font-medium">Equip.</th>
          <th className="text-left px-3 py-2.5 font-medium">Broker</th>
          {showPartners && (
            <th className="text-center px-3 py-2.5 font-medium">Partner</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {results.map((result, index) => {
          const rowKey = result.id || result.entry_id || index;
          const paymentValue =
            toNumber(result.payment) ??
            toNumber(result.rate) ??
            toNumber(result.sourceCostPerUnit);
          const distanceValue = toNumber(result.distance);
          const originDeadheadValue =
            toNumber(result.origin_distance) ?? toNumber(result.dead_head);

          const distance = distanceValue || 0;
          const deadhead = originDeadheadValue || 0;
          const payment = paymentValue || 0;

          const rpmWithDeadhead =
            distance > 0 || deadhead > 0
              ? (payment / (distance + deadhead)).toFixed(2)
              : result.rate_per_mile_est
                ? Number(result.rate_per_mile_est).toFixed(2)
                : "N/A";

          const rpmWithoutDeadhead =
            distance > 0
              ? (payment / distance).toFixed(2)
              : result.rate_per_mile_est
                ? Number(result.rate_per_mile_est).toFixed(2)
                : "N/A";

          const isHighlighted = Boolean(
            highlightMap && highlightMap[rowKey],
          );

          // Check if broker is TQL (case-insensitive)
          const companyName = capitalizeCompanyName(result.company_name) || "";

          // Truncate broker name if longer than MAX_BROKER_LENGTH
          const truncatedBrokerName =
            companyName && companyName.length > MAX_BROKER_LENGTH
              ? `${companyName.substring(0, MAX_BROKER_LENGTH)}...`
              : companyName;

          // Determine partner display
          const partnerDisplay = getDataSourceBadge(
            result.data_source || result.source,
            "light"
          );

          // Check if load has been viewed
          const loadId = getLoadIdentifier(result);
          const isViewed = loadId ? viewedLoadIds.has(String(loadId)) : false;
          const isSelected = loadId ? selectedLoadIds.has(String(loadId)) : false;

          // Handle row click based on select mode
          const handleRowClick = () => {
            if (isSelectMode && onToggleLoadSelection) {
              onToggleLoadSelection(loadId);
            } else {
              onSelectLoad(result);
            }
          };

          const handleRowKeyDown = (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleRowClick();
            }
          };

          return (
            <tr
              key={rowKey}
              className={`hover:bg-tw-info/5 transition-colors ${
                result.isBackhaul ? "bg-tw-secondary/5" : ""
              } ${isSelected ? "bg-tw-info/10" : ""} cursor-pointer focus:outline-none focus:ring-2 focus:ring-tw-info/60 ${
                isHighlighted ? "animate-new-load" : ""
              }`}
              onClick={handleRowClick}
              tabIndex={0}
              onKeyDown={handleRowKeyDown}
              aria-selected={isSelectMode ? isSelected : undefined}
            >
              {isSelectMode && (
                <td className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleLoadSelection(loadId)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-tw-neutral-300 text-tw-info focus:ring-tw-info cursor-pointer"
                    aria-label={`Select load ${loadId}`}
                  />
                </td>
              )}
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                <span className="flex items-center gap-1.5">
                  {!isViewed && (
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 bg-tw-info"
                      aria-label="New load"
                      title="Not viewed"
                    />
                  )}
                  <span>{result.age && result.age > 0 ? `${result.age}h` : "<1h"}</span>
                </span>
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {result.origin_city || "—"}, {result.origin_state || "—"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {result.destination_city || "—"}, {result.destination_state || "—"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                ${paymentValue !== null && paymentValue !== undefined ? paymentValue : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {rpmWithoutDeadhead !== "N/A" ? `$${rpmWithoutDeadhead}/mi` : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {rpmWithDeadhead !== "N/A" ? `$${rpmWithDeadhead}/mi` : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {result.origin_distance != null ? `${result.origin_distance} mi` : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {result.distance ? `${result.distance} mi` : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {result.weight
                  ? `${Math.round(result.weight).toLocaleString()}`
                  : "N/A"}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {getTruckTypeLabel(result.equipment_type || result.truck_type)}
              </td>
              <td className="px-3 py-2 text-tw-neutral-900 leading-normal">
                {truncatedBrokerName || "N/A"}
              </td>
              {showPartners && (
                <td className="px-3 py-2 text-tw-neutral-900 leading-normal text-center">
                  {partnerDisplay}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ResultsTable;